// Per-weapon feel: what one trigger pull does to the lens, the pad and the trigger.
//
// Benchmarks (what each number is chasing):
//   Destiny 2 hand cannons: a sharp up-kick that recovers almost fully before the next shot,
//     so the gun feels heavy but the reticle stays honest. -> Big Iron, Starter.
//   Doom Eternal Super Shotgun / Hunt: Showdown: a big pitch kick + FOV punch + roll, the lens
//     "breathes out" with the blast. -> Gentlemen, Scoundrel.
//   Battlefield / Hunt bolt actions: a slow, heavy punch with a long settle. -> Bolt-Action.
//   Deep Rock Galactic / Helldivers 2 MGs: small per-round punch with a random yaw walk and a
//     trigger that buzzes while held. -> Auto MG, Akimbo.
// The punch only rotates the camera (camera.ts punch spring); the shot's aim is never moved,
// so a controller player's crosshair stays where they put it.

import { W } from '../../sim/weapons';
import { TRIG, Trig } from './dualsense';

export const enum Shell { None = 0, Pistol = 1, Rifle = 2, Shotgun = 3 }

export interface Feel {
  /** peak lens pitch (deg) and random yaw (deg), roll (deg), fov punch (deg) */
  pitch: number; yaw: number; roll: number; fov: number;
  /** spring rate: higher snaps back faster (1/w s to peak) */
  w: number;
  /** trauma shake added (0..1) */
  shake: number;
  /** pad rumble: big motor, small motor, ms */
  rumble: [number, number, number];
  /** cowboy chest kick multiplier on top of the class recoil */
  body: number;
  shell: Shell;
  /** R2 at rest and while the trigger is being held on an automatic */
  r2: () => Trig;
  r2Fire?: () => Trig;
}

const F = (f: Partial<Feel>): Feel => ({
  pitch: 0.8, yaw: 0.2, roll: 0.3, fov: 0, w: 26, shake: 0, rumble: [0.2, 0.3, 60], body: 1, shell: Shell.Pistol,
  r2: () => TRIG.weapon(4, 6, 3), ...f,
});

const ROCKET = F({ pitch: 2.6, yaw: 0.6, roll: 1.4, fov: 3, w: 12, shake: 0.32, rumble: [0.9, 0.5, 240], body: 1.6, shell: Shell.None, r2: () => TRIG.weapon(5, 8, 8) });
const LOB = F({ pitch: 0.9, yaw: 0.2, roll: 0.4, fov: 0.8, w: 16, shake: 0.06, rumble: [0.4, 0.2, 110], body: 0.6, shell: Shell.None, r2: () => TRIG.weapon(3, 5, 4) });

export const FEEL: Record<number, Feel> = {
  [W.Starter]: F({ pitch: 0.9, yaw: 0.2, roll: 0.35, fov: 0.3, w: 30, rumble: [0.12, 0.35, 55], shell: Shell.Pistol, r2: () => TRIG.weapon(4, 6, 3) }),
  [W.PulseRifle]: F({ pitch: 0.45, yaw: 0.18, roll: 0.15, fov: 0.2, w: 34, rumble: [0.08, 0.3, 45], shell: Shell.None, body: 0.7, r2: () => TRIG.weapon(3, 5, 4) }),
  [W.AutoMG]: F({ pitch: 0.42, yaw: 0.32, roll: 0.25, fov: 0.15, w: 30, shake: 0.02, rumble: [0.22, 0.32, 70], shell: Shell.Rifle, body: 0.9,
    r2: () => TRIG.feedback(3, 3), r2Fire: () => TRIG.vibrate(3, 6, 13) }),
  [W.BoltSniper]: F({ pitch: 3.4, yaw: 0.5, roll: 1.6, fov: 3.2, w: 11, shake: 0.18, rumble: [0.75, 0.6, 200], body: 1.7, shell: Shell.Rifle, r2: () => TRIG.weapon(5, 8, 8) }),
  [W.Gentlemen]: F({ pitch: 3.2, yaw: 0.8, roll: 2.2, fov: 4.2, w: 14, shake: 0.22, rumble: [0.85, 0.55, 180], body: 1.8, shell: Shell.None, r2: () => TRIG.weapon(3, 6, 7) }),
  [W.BigIron]: F({ pitch: 2.4, yaw: 0.45, roll: 1.0, fov: 1.6, w: 18, shake: 0.08, rumble: [0.55, 0.5, 130], body: 1.5, shell: Shell.None, r2: () => TRIG.weapon(4, 7, 6) }),
  [W.DuelBlaster]: F({ pitch: 1.2, yaw: 0.25, roll: 0.5, fov: 1.2, w: 20, rumble: [0.3, 0.55, 90], body: 1.1, shell: Shell.None,
    r2: () => TRIG.feedback(2, 4), r2Fire: () => TRIG.vibrate(5, 4, 40) }),
  [W.Akimbo]: F({ pitch: 0.35, yaw: 0.35, roll: 0.5, fov: 0.1, w: 34, rumble: [0.1, 0.35, 50], body: 0.8, shell: Shell.Pistol,
    r2: () => TRIG.feedback(4, 2), r2Fire: () => TRIG.vibrate(4, 5, 9) }),
  [W.Scoundrel]: F({ pitch: 3.8, yaw: 1.0, roll: 2.6, fov: 4.6, w: 13, shake: 0.26, rumble: [0.95, 0.6, 200], body: 2, shell: Shell.None, r2: () => TRIG.weapon(2, 5, 8) }),
  [W.Harpoon]: F({ pitch: 1.8, yaw: 0.3, roll: 0.8, fov: 1.5, w: 14, shake: 0.1, rumble: [0.6, 0.4, 150], body: 1.4, shell: Shell.None, r2: () => TRIG.weapon(4, 7, 7) }),
  [W.Turret]: LOB, [W.BigOne]: LOB,
  [W.BadIdea]: F({ ...ROCKET, pitch: 3.6, fov: 5, shake: 0.5, rumble: [1, 0.8, 320] }),
};
for (const id of [W.Rocket, W.RocketFire, W.RocketIce, W.RocketShock, W.RocketAcid, W.RocketMag]) FEEL[id] = ROCKET;

const DEFAULT = LOB;
export function feelOf(wid: number): Feel { return FEEL[wid] ?? DEFAULT; }

/** L2 while aiming is possible: a soft pull that firms up at the end (the sight settling in). */
export const L2_AIM = () => TRIG.feedback(3, 2);
